import { useEffect, useRef, useState } from 'react'

// Per SMT-1646. Two phase pools of decorative loading messages; the hook
// builds a randomized sequence on each activation, cycles through with a
// per-message random interval, and parks on FINAL_MESSAGE once the
// sequence is exhausted (so long-running fetches don't go silent).

const PHASE_1 = [
  'Gathering data',
  'Compiling results',
  'Processing records',
  'Preparing your table',
  'Organizing data',
  'Loading dataset'
]

const PHASE_2 = [
  'Structuring results',
  'Reviewing parameters',
  'Aggregating information',
  'Fetching latest data',
  'Validating data',
  'Sorting records',
  'Building table view'
]

const FINAL_MESSAGE = 'Finalizing results'

const MIN_INTERVAL_MS = 975
const MAX_INTERVAL_MS = 5000

/**
 * Pick `n` distinct elements from `pool` uniformly at random using a
 * partial Fisher-Yates shuffle. Returns a new array; the input is not
 * mutated.
 *
 * @param {string[]} pool
 * @param {number} n - must satisfy 0 <= n <= pool.length
 * @returns {string[]}
 */
export function pickRandom (pool, n) {
  if (n <= 0) return []
  const out = [...pool]
  for (let i = out.length - 1; i > out.length - 1 - n; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  // slice(out.length - n) instead of slice(-n) because slice(-0) returns
  // the whole array (JS quirk: -0 === 0). The n<=0 guard above covers it,
  // but keep the explicit form for clarity.
  return out.slice(out.length - n)
}

// Number of distinct messages drawn from each phase pool. Fixed (not
// randomized) so the sequence has a predictable cadence.
const PER_PHASE_COUNT = 3

/**
 * Build one cycle's message sequence: 3 picks from PHASE_1, then 3 picks
 * from PHASE_2, then FINAL_MESSAGE as the parking spot.
 *
 * @returns {string[]} length always 7, last entry always FINAL_MESSAGE.
 */
export function buildSequence () {
  return [
    ...pickRandom(PHASE_1, PER_PHASE_COUNT),
    ...pickRandom(PHASE_2, PER_PHASE_COUNT),
    FINAL_MESSAGE
  ]
}

/**
 * Random integer in [MIN_INTERVAL_MS, MAX_INTERVAL_MS], inclusive.
 *
 * @returns {number}
 */
export function randomInterval () {
  return MIN_INTERVAL_MS + Math.floor(Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS + 1))
}

/**
 * Returns the current loading message, or `null` when inactive. Each time
 * `active` flips true, a fresh randomized sequence is built and the hook
 * cycles through it with a chained setTimeout. The last message
 * (FINAL_MESSAGE = "Finalizing results") stays put if the fetch outlasts
 * the sequence. When `active` flips back to false, the message resets to
 * null and any pending timer is cleared.
 *
 * @param {boolean} active
 * @returns {string | null}
 */
export function useLoadingMessage (active) {
  const [message, setMessage] = useState(null)
  const timeoutRef = useRef(null)

  useEffect(() => {
    if (!active) {
      setMessage(null)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      return undefined
    }

    const sequence = buildSequence()
    let index = 0

    const step = () => {
      setMessage(sequence[index])
      if (index < sequence.length - 1) {
        index += 1
        timeoutRef.current = setTimeout(step, randomInterval())
      } else {
        timeoutRef.current = null
      }
    }
    step()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [active])

  return message
}

// Exported for testing.
export const __test__ = { PHASE_1, PHASE_2, FINAL_MESSAGE, MIN_INTERVAL_MS, MAX_INTERVAL_MS }
